import * as React from 'react'

import { type SelectOption } from '@factorialco/deprecated-design-system'

import type { RegularSelectOptionsQuery } from 'generated/resources/graphql'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { cotizationGroups } from 'modules/core/lib/cotizationGroups'
import countryList from 'modules/core/lib/countryList'
import i18n from 'modules/core/lib/i18n'

import { TriggerAttributes } from 'modules/automations/lib/triggerAttributes'
import ContractVersion from 'modules/contracts/store/models/ContractVersion'
import getTitle from 'modules/documents/components/Documents/getTitle'
import folders from 'modules/documents/store/collections/folders'
import { ALL_TYPES } from 'modules/documents/store/models/Document'
import Review from 'modules/documents/store/models/Review'
import jobRolesCollection from 'modules/job_catalog/store/collections/roles'
import type { Attributes as JobRoleAttributes } from 'modules/job_catalog/store/models/JobRole'
import locations from 'modules/locations/store/collections/locations'
import teams from 'modules/teams/store/collections/teams'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'

interface CountryNameableWithLegalEntity {
  id: string
  name: string
  legalEntity?: { country: string; legalName: string } | null
}

const countryNameableToOptions = (items: CountryNameableWithLegalEntity[]): SelectOption[] => {
  return items.reduce<SelectOption[]>((acc, item) => {
    const legalEntity = item.legalEntity
    if (legalEntity == null) return acc
    acc.push({
      value: `${legalEntity.country}_${item.id}`,
      label: `[${legalEntity.legalName}] ${item.name}`,
    })
    return acc
  }, [])
}

export const reviewStatusOptions = (): SelectOption[] => {
  return [
    {
      label: i18n.t('reviews.status.approved'),
      value: Review.Status.approved,
    },
    {
      label: i18n.t('reviews.status.rejected'),
      value: Review.Status.rejected,
    },
  ]
}

export const countryOptions = (): SelectOption[] => {
  return countryList[i18n.locale as keyof typeof countryList] || countryList.en
}

export const booleanOptions = (attr: string): SelectOption[] => {
  const falseLabel =
    attr === TriggerAttributes.document_public ? i18n.t('common.private') : i18n.t('common.false')

  const trueLabel =
    attr === TriggerAttributes.document_public ? i18n.t('common.public') : i18n.t('common.true')

  return [
    {
      label: trueLabel,
      value: true,
    },
    {
      label: falseLabel,
      value: false,
    },
  ]
}

export const genderOptions = (): SelectOption[] => {
  return [
    {
      label: i18n.t('common.female'),
      value: 'female',
    },
    {
      label: i18n.t('common.male'),
      value: 'male',
    },
  ]
}

export function LeaveTypeOptions(): SelectOption[] {
  useAsyncData(() => leaveTypes.fetch(), [])

  return leaveTypes.map((leaveType: LeaveType) => ({
    value: leaveType.id,
    label: leaveType.name,
  }))
}

export function LeaveTypeDefaultOptions(): SelectOption[] {
  useAsyncData(() => leaveTypes.fetch(), [])

  return leaveTypes.map((leaveType: LeaveType) => ({
    value: leaveType.attributes.identifier,
    label: leaveType.name,
  }))
}

export function LocationOptions(): SelectOption[] {
  useAsyncData(() => locations.fetch(), [])

  return locations.map((location) => ({
    value: location.attributes.id,
    label: location.attributes.name,
  }))
}

export function TeamOptions(): SelectOption[] {
  useAsyncData(() => teams.fetch(), [])

  return teams.map((team) => ({
    value: team.attributes.id,
    label: team.attributes.name,
  }))
}

export function DocumentFolderOptions(): SelectOption[] {
  useAsyncData(() => folders.fetch({ data: { active: true } }), [])

  return folders.map((folder) => ({
    value: folder.attributes.id,
    label: folder.attributes.name,
  }))
}

export function documentTypeOptions(): SelectOption[] {
  return Object.keys(ALL_TYPES).map((type) => ({
    value: ALL_TYPES[type as keyof typeof ALL_TYPES],
    label: getTitle(type),
  }))
}

export const employeeActiveOptions = () => {
  return [
    {
      label: i18n.t('automations.options.employees.status.active'),
      value: 'true',
    },
    {
      label: i18n.t('automations.options.employees.status.terminated'),
      value: 'false',
    },
  ]
}

// For backward compatibility
export const SpainProfessionalCategories = (
  contractData?: RegularSelectOptionsQuery['contracts']
): SelectOption[] => {
  return (contractData?.spanishProfessionalCategories ?? []).map((category) => ({
    value: category.id,
    label: `[${category.legalEntity?.legalName}] ${category.name}`,
  }))
}

export const AllProfessionalCategories = (
  contractData?: RegularSelectOptionsQuery['contracts']
): SelectOption[] => {
  return [
    ...countryNameableToOptions(contractData?.spanishProfessionalCategories ?? []),
    ...countryNameableToOptions(contractData?.frenchProfessionalCategories ?? []),
  ]
}

export const AllWorkingDayTypes = (
  contractData?: RegularSelectOptionsQuery['contracts']
): SelectOption[] => {
  return [
    ...countryNameableToOptions(contractData?.spanishWorkingDayTypes ?? []),
    ...countryNameableToOptions(contractData?.frenchWorkTypes ?? []),
  ]
}

export const EmployeeEducationLevelOptions = (
  contractData?: RegularSelectOptionsQuery['contracts']
): SelectOption[] => {
  return countryNameableToOptions(contractData?.spanishEducationLevels ?? [])
}

export const JobCatalogRoles = (): SelectOption[] => {
  const [roles, setRoles] = React.useState<SelectOption[]>([])

  useAsyncData(() => {
    return jobRolesCollection.fetch().then((roles) => {
      const jobRolesOptions = roles.map((job: JobRoleAttributes) => {
        return {
          value: job.id,
          label: job.name,
        }
      })

      setRoles(jobRolesOptions)
    })
  }, [])

  return roles
}

export const CotizationGroupOptions = (): SelectOption[] => {
  return cotizationGroups()
}

export const SalaryFrequencyOptions = (): SelectOption[] => {
  return Object.keys(ContractVersion.SalaryFrequencies()).map((item) => {
    return { value: item, label: item }
  })
}

export const workingHoursFrequencyOptions = (): SelectOption[] => {
  return [
    {
      label: i18n.t('automations.options.working_hours_frequency.day'),
      value: 'day',
    },
    {
      label: i18n.t('automations.options.working_hours_frequency.week'),
      value: 'week',
    },
    {
      label: i18n.t('automations.options.working_hours_frequency.month'),
      value: 'month',
    },
    {
      label: i18n.t('automations.options.working_hours_frequency.year'),
      value: 'year',
    },
  ]
}

export const AllContractTypes = (
  contractData?: RegularSelectOptionsQuery['contracts']
): SelectOption[] => {
  return [
    ...countryNameableToOptions(contractData?.spanishContractTypes ?? []),
    ...countryNameableToOptions(contractData?.germanContractTypes ?? []),
    ...countryNameableToOptions(contractData?.frenchContractTypes ?? []),
    ...countryNameableToOptions(contractData?.portugueseContractTypes ?? []),
  ]
}
